/* global URL_MENU */
import { useState, useCallback, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import type { ConnectionHookResult, ProviderCode } from '@interfaces/Integration';
import { MetaCloudConnectionState, MetaCloudConnectionResponse, MetaCloudConnectPhase } from '../types';
import { useSyncIntegration, useRemoveIntegration, useGetIntegrations } from '../../../../hooks';
import { PROVIDER_TYPE_MAP, Integration } from '@utils/constants';
import { useCommerceFromStore } from '@store/actions/commerce';
import { loadFacebookSdk, launchEmbeddedSignup, getSignupStepLabel } from '../constants';
import { getServerErrorMessage } from '../errors';
import { isConfigPhoneHealthy } from '../phoneNumberStatus';

const INITIAL_STATE: MetaCloudConnectionState = {
  connectionStatus: 'DISCONNECTED',
  phoneNumber: null,
  instanceName: null,
  error: null,
};

/**
 * Insignia que le toca a la tarjeta, con una precedencia explícita.
 *
 * Existe porque en la misma tarjeta convivían mal TRES señales —conectado, requiere
 * atención y (nueva) conectando— y resolverlas por separado deja combinaciones
 * absurdas: "CONECTADO en verde" mientras el diálogo de Meta está abierto, o el ámbar
 * de salud tapando que hay una reconexión corriendo. La regla es una sola y va en
 * orden: lo que está PASANDO gana sobre lo que está MAL, y lo que está mal gana sobre
 * lo que está guardado.
 *
 * @param {MetaCloudConnectPhase} phase Operación en vuelo.
 * @param {boolean} needsAttention El número no puede enviar según Meta.
 * @param {MetaCloudConnectionStatus} connectionStatus Estado persistido de la conexión.
 * @returns {string} Estado que consume `ConnectionStatusBadge`.
 */
const resolveBadgeStatus = (
  phase: MetaCloudConnectPhase,
  needsAttention: boolean,
  connectionStatus: MetaCloudConnectionState['connectionStatus'],
): string => {
  if (phase === 'AWAITING_META') return 'CONNECTING';
  if (phase === 'EXCHANGING_CODE') return 'EXCHANGING_CODE';
  if (needsAttention) return 'NEEDS_ATTENTION';
  return connectionStatus;
};

interface UseMetaCloudConnectionProps {
  id?: string;
  provider_code: ProviderCode;
  provider_type: keyof typeof PROVIDER_TYPE_MAP;
  configuration: Record<string, unknown>;
}

/**
 * `reconnect` se declara REQUERIDO en el tipo de retorno (en `ConnectionHookResult` es
 * opcional, porque no todo proveedor puede rehacer su alta sin desconectar). Este hook
 * siempre lo entrega, y afirmarlo aquí le ahorra a cada consumidor un fallback del
 * estilo `reconnect || connect` que solo existiría para contentar a TypeScript.
 */
type MetaCloudConnection = ConnectionHookResult & { reconnect: () => void };

export const useMetaCloudConnection = (integration?: UseMetaCloudConnectionProps): MetaCloudConnection => {
  const { syncIntegration } = useSyncIntegration();
  const { removeIntegration } = useRemoveIntegration();
  const { refreshIntegrations } = useGetIntegrations();
  const commerce = useCommerceFromStore();

  const lastCheckedIdRef = useRef<string | null>(null);
  const isDisconnectingRef = useRef(false);

  const [isCheckingStatus, setIsCheckingStatus] = useState(() => Boolean(integration?.id));

  // Eje independiente del estado de conexión: qué está corriendo AHORA. Ver `types.ts`.
  const [phase, setPhase] = useState<MetaCloudConnectPhase>('IDLE');

  const [state, setState] = useState<MetaCloudConnectionState>(() => {
    if (integration?.id) {
      return {
        ...INITIAL_STATE,
        connectionStatus: 'CONNECTED',
        instanceName: (integration.configuration?.instance_name as string) || null,
        phoneNumber: (integration.configuration?.phone_number as string) || null,
      };
    }
    return { ...INITIAL_STATE };
  });

  const handleConnectionResponse = useCallback(
    (response: MetaCloudConnectionResponse, showToast = false) => {
      if (response.status === 'CONNECTED') {
        setState((prev) => {
          if (isDisconnectingRef.current) return prev;
          if (prev.connectionStatus === 'CONNECTED') return prev;

          return {
            ...prev,
            connectionStatus: 'CONNECTED',
            instanceName: response.instanceName || null,
            phoneNumber: response.phoneNumber || null,
            error: null,
          };
        });

        if (!isDisconnectingRef.current) {
          refreshIntegrations();
          if (showToast) {
            toast.success('¡WhatsApp Cloud API conectado!');
          }
        }
      } else if (response.status === 'DISCONNECTED' || response.status === 'ERROR') {
        setState((prev) => ({
          ...prev,
          // Preservar 'ERROR' del backend para que MetaConnectErrorNote muestre el error;
          // mapearlo a 'DISCONNECTED' lo ocultaría (solo se vería en excepciones de red).
          connectionStatus: response.status === 'ERROR' ? 'ERROR' : 'DISCONNECTED',
          error: response.message || 'Desconectado',
        }));
      }
    },
    [refreshIntegrations],
  );

  const fetchStatus = useCallback(async () => {
    try {
      const response = (await syncIntegration(integration as unknown as Integration, {
        action: 'SHOW_STATUS',
        notify: false,
      })) as unknown as MetaCloudConnectionResponse;

      if (response) {
        handleConnectionResponse(response);
        return response;
      }
    } catch {
      return null;
    } finally {
      setIsCheckingStatus(false);
    }
  }, [integration, syncIntegration, handleConnectionResponse]);

  // Check real status on mount — once per ID
  useEffect(() => {
    if (integration?.id && integration.id !== lastCheckedIdRef.current) {
      lastCheckedIdRef.current = integration.id;
      fetchStatus();
    }
  }, [integration?.id, fetchStatus]);

  // Preload Facebook SDK when component mounts
  useEffect(() => {
    loadFacebookSdk().catch(() => {
      // SDK load failure is non-fatal; error will surface when user clicks Connect
    });
  }, []);

  /**
   * Opens Meta's Embedded Signup popup via FB.login().
   * The code comes back via the JS callback — no redirect needed.
   */
  const connect = useCallback(async () => {
    // Resetear el estado de error en el reintento: si quedó en 'ERROR', limpiarlo a
    // 'DISCONNECTED' para no mostrar el botón en loading y el mensaje de error a la vez.
    setState((prev) => ({
      ...prev,
      error: null,
      connectionStatus: prev.connectionStatus === 'ERROR' ? 'DISCONNECTED' : prev.connectionStatus,
    }));
    // A partir de aquí la tarjeta deja de verse igual que hace un segundo: hay un
    // diálogo de Meta abierto y la insignia lo dice.
    setPhase('AWAITING_META');

    try {
      await loadFacebookSdk();
      // Precarga la pantalla "Completa la información de tu empresa" del diálogo de Meta con
      // los datos del comercio en sesión. El comercio no tiene campo de sitio web propio: su
      // URL pública es el menú (`URL_MENU/id_path`), que es lo que Meta debe mostrar.
      const result = await launchEmbeddedSignup({
        name: commerce?.nombre,
        email: commerce?.correo_contacto,
        website: commerce?.id_path ? `${URL_MENU}/${commerce.id_path}` : '',
        country: commerce?.address?.country,
      });

      // El comercio se salió del diálogo. La tarjeta vuelve LIMPIA a como estaba: nada
      // de quedarse clavada en "Conectando…" ni de degradar una conexión que sigue
      // intacta. Y se le dice dónde se quedó, que es lo que le permite retomar.
      if (result.status === 'CANCELLED') {
        setPhase('IDLE');
        const stepLabel = getSignupStepLabel(result.currentStep);
        toast(stepLabel ? `Saliste de la conexión con Meta en ${stepLabel}.` : 'Cancelaste la conexión con Meta.', {
          icon: 'ℹ️',
        });
        return;
      }

      // Meta nos dijo POR QUÉ falló. Ese texto vale mucho más que un "algo salió mal":
      // es lo único que le dice al comercio qué arreglar en su cuenta.
      if (result.status === 'ERROR') {
        setPhase('IDLE');
        const detail = result.errorMessage || 'Meta rechazó la conexión sin dar un motivo.';
        setState((prev) => ({ ...prev, error: detail }));
        toast.error(`Meta no completó la conexión: ${detail}`);
        return;
      }

      // Ya tenemos el `code`. Segunda —y última— fase honesta: el canje en NUESTRO
      // backend. Es nuestra, así que sí la podemos afirmar.
      setPhase('EXCHANGING_CODE');

      // Exchange code for token via backend. En Coexistence el popup solo devuelve
      // `waba_id`; el backend resuelve el `phone_number_id` desde la WABA si no llega.
      const response = (await syncIntegration(
        (integration || {
          provider_code: 'metaCloudApi',
          provider_type: 'communication',
        }) as unknown as Integration,
        {
          action: 'CONNECT',
          notify: false,
          config: {
            code: result.code,
            waba_id: result.wabaId,
            phone_number_id: result.phoneNumberId,
            featureType: result.featureType,
          },
        },
      )) as unknown as MetaCloudConnectionResponse;

      if (response) {
        handleConnectionResponse(response, true);
      }
      setPhase('IDLE');
    } catch (err) {
      // El backend manda el diagnóstico real en el body del 4xx ("No se encontraron
      // numeros de telefono...", "No se encontro una cuenta de WhatsApp Business
      // compartida..."). Sustituirlo siempre por el genérico dejaba al comercio sin
      // saber qué arreglar en su cuenta de Meta; el genérico queda solo de fallback.
      const message = getServerErrorMessage(err, 'Error al conectar con Meta. Intenta nuevamente.');

      // Alta NUEVA vs RE-conexión: son dos fallos distintos y no pueden terminar igual.
      //
      // Si ya había una integración (`integration.id`), el alta es aditiva de punta a
      // punta: `handleConnect` hace spread de la config previa sin ningún teardown, y
      // `functions/sync.js` solo persiste cuando la acción devuelve — un throw nunca
      // llega al UPDATE. O sea: la conexión anterior sigue viva en el servidor.
      // Bajar el estado a 'ERROR' aquí mentía sobre eso: la card oculta "Ajustes" y
      // "Desconectar" y pinta "Conectar", que es exactamente la impresión de haberse
      // quedado sin integración. El error se comunica por toast y el estado se queda
      // donde estaba.
      const hasLiveIntegration = Boolean(integration?.id);

      setState((prev) => ({
        ...prev,
        connectionStatus: hasLiveIntegration ? prev.connectionStatus : 'ERROR',
        error: message,
      }));
      setPhase('IDLE');

      if (hasLiveIntegration) {
        toast.error(`No se pudo volver a conectar: ${message} Tu conexión actual sigue activa.`);
      }
    }
  }, [integration, syncIntegration, handleConnectionResponse, commerce]);

  const disconnect = useCallback(async () => {
    isDisconnectingRef.current = true;

    if (!integration?.id) {
      lastCheckedIdRef.current = null;
      setState(INITIAL_STATE);
      isDisconnectingRef.current = false;
      return;
    }

    setPhase('DISCONNECTING');

    try {
      await removeIntegration(integration as unknown as Integration);
      lastCheckedIdRef.current = null;
      setState(INITIAL_STATE);
    } catch {
      // `useRemoveIntegration` ya avisa por toast; aquí solo importa no dejar la fase
      // colgada ni propagar el rechazo al confirmador del botón.
    } finally {
      setPhase('IDLE');
      isDisconnectingRef.current = false;
    }
  }, [integration, removeIntegration]);

  const isConnected = state.connectionStatus === 'CONNECTED';

  // La insignia deja de repetir lo que el hook cree y pasa a repetir lo que Meta dice.
  // `connection_status` guardado solo prueba que el intercambio de token salió bien;
  // `status`/`platform_type` (leídos de Graph) prueban si el número puede enviar. Con
  // los dos en desacuerdo, la card mostraba CONECTADO en verde junto a un aviso ámbar
  // que decía lo contrario. Sin dato de Graph no se degrada nada: ver `isConfigPhoneHealthy`.
  const needsAttention = isConnected && !isConfigPhoneHealthy(integration?.configuration || {});

  return {
    isConnected,
    // Una sola fuente para "hay algo corriendo". Antes vivía en el estado de conexión
    // y solo la consumía la rama de "no conectado" de la pantalla de configuración: por
    // eso la tarjeta de un comercio YA conectado se veía idéntica antes, durante y
    // después del diálogo de Meta.
    isConnecting: phase !== 'IDLE',
    isCheckingStatus,
    connect,
    disconnect,
    // Misma operación que `connect`: el CONNECT del backend es idempotente y aditivo
    // (reescribe credenciales sobre la config existente, sin DELETE previo), así que
    // reconectar NO necesita —ni debe— desconectar primero. Se expone con otro nombre
    // porque es lo que habilita el botón "Volver a conectar" en la card.
    //
    // El payload viaja SIN `disconnect_previous`: esa bandera es el corte consentido
    // hacia OTRO proveedor, no una reconexión del mismo. Mandarla aquí apagaría la
    // integración que se está reparando.
    reconnect: connect,
    status: resolveBadgeStatus(phase, needsAttention, state.connectionStatus),
    instanceName: state.instanceName,
    // Se expone para que la card pueda pintar el motivo real del fallo. Sin esto el
    // mensaje del servidor moría dentro del hook.
    error: state.error,
    // OAuth popup flow: no QR needed
    isQrVisible: false,
    qrCode: null,
    onVerify: undefined,
    onCloseQr: undefined,
    isVerifying: false,
  };
};
